import React, { useState, useMemo } from 'react'
import ReactDOM from 'react-dom/client'
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api'
import darkMapStyle from './bs-map-style'

const root = ReactDOM.createRoot(document.querySelector('#homesApp'))
const googleMapsApiKey = siteData.googleMapsApiKey

console.log(appData.projects)

const containerStyle = {
  width: '100%',
  height: '100%',
}

const center = {
  lat: 49.253529,
  lng: -122.966617,
}

const categories = Array.from(
  new Map(
    appData.projects.map(p => [p.status.value, p.status])
  ).values()
).sort((a, b) => {
  // Custom order: coming_soon first, then selling, then sold
  const order = ['coming_soon', 'selling', 'sold']
  return order.indexOf(a.value) - order.indexOf(b.value)
})

const MapModule = ({ projectData, selectedCategory, handleMapLoad }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey
  })

  const [activeMarkerId, setActiveMarkerId] = useState(null)

  const pinkSquareSvg = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11"><rect width="11" height="11" fill="%23d40f7d"/></svg>';

  const filteredMarkers = useMemo(() => {
    return projectData.filter(project => project.status.value === selectedCategory.value)
  }, [projectData, selectedCategory])

  if (loadError) return <div>Error loading maps</div>
  if (!isLoaded) return <div>Loading...</div>

  // Find the active marker object
  const activeMarker = filteredMarkers.find(marker => marker.id === activeMarkerId)

  return (
    <div className="map-container">
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={14}
        options={{
          styles: darkMapStyle,
          disableDefaultUI: true,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          backgroundColor: '#212121'
        }}
        onLoad={handleMapLoad}
        onClick={() => setActiveMarkerId(null)} // Close info window when map is clicked
      >
        {filteredMarkers.map(marker => (
          <Marker 
            key={marker.id} 
            position={marker.map_location}
            icon={pinkSquareSvg}
            onClick={() => setActiveMarkerId(marker.id)}
            onMouseOver={() => setActiveMarkerId(marker.id)}
          />
        ))}
        {activeMarker && (
          <InfoWindow
            position={activeMarker.map_location}
            onCloseClick={() => setActiveMarkerId(null)}
          >
            <div className='info-window'>
              <button
                style={{
                  float: 'right',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  marginLeft: '8px',
                  padding: 0,
                  lineHeight: 0
                }}
                aria-label="Close"
                onClick={() => setActiveMarkerId(null)}
              >
                <svg width="15" height="15" viewBox="0 0 15 15" aria-hidden="true" focusable="false">
                  <line x1="3" y1="3" x2="12" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="12" y1="3" x2="3" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <span className="project-name">{activeMarker.name}</span><br />
              <a href={`#project-${activeMarker.id}`}>View Info</a>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  )
}

const ProjectInfoModule = ({ projectData, selectedCategory }) => {
  const filteredProjects = projectData.filter(project => project.status.value === selectedCategory.value)

  return (
    <section
      className={
        `projects-info container-fluid headline-black ${selectedCategory.value}`
      }
    >
      <h1>
        { 
          selectedCategory.value === 'selling' ? <>Now <span className="text-primary">Selling</span></> :
          selectedCategory.value === 'coming_soon' ? <>Coming <span className="text-primary">Soon</span></> :
          selectedCategory.value === 'sold' ? <>Past <span className="text-primary">Projects</span></> : ''
        }
      </h1>
      <ul className="projects-list">
        {filteredProjects.map(project => (
          <li key={project.id} id={`project-${project.id}`} className="project-item">
            <h3>{project.name}</h3>
            <div className="thumbnail-container">
              <a href={project.permalink} className="thumbnail-link">
                <img src={project.thumbnail} alt={project.name} />
                <div className="hover-shade"></div>
                <div className="logo">
                  { project.logo && <img src={project.logo} alt={`${project.name} logo`} /> }
                </div>
              </a>
            </div>
            <div className="project-info">
              <h3>{project.name}</h3>
              { project.status.value === 'sold' && project.year_completed && <p className="year-completed">Completed {project.year_completed}</p> }
              <p>{ project.one_line_description }</p>
              { project.status.value !== 'sold' &&
                <a href={project.permalink} className="btn">
                  View
                  <svg className="link-arrow" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13"><path d="M1.1099,2.38977l.00141-1.11299c.00026-.46852.38001-.84827.84853-.84853l9.76232-.00424c.46852.00026.84827.38001.84853.84853l.00707,9.76656c-.00026.46852-.38001.84827-.84853.84853h-1.11581c-.47165.00176-.85542-.37916-.85718-.85081-.00003-.00678.00003-.01357.00017-.02035l.14354-5.91071-7.21956,7.21956c-.33155.33312-.87038.33438-1.20349.00283h0s-.80398-.80398-.80398-.80398c-.33312-.33155-.33438-.87038-.00283-1.20349.00094-.00095.00189-.00189.00283-.00283L7.89247,3.09829l-5.91424.14001c-.46764.01178-.85628-.35777-.86806-.8254-.00019-.00771-.00028-.01542-.00027-.02313Z"/></svg>
                </a>
              }
            </div>
          </li>
        ))}
      </ul>
    </section>
  )
}

const App = () => {
  const [selectedCategory, setSelectedCategory] = useState({ value: 'coming_soon', label: 'Coming Soon' })
  const [mapRef, setMapRef] = useState(null)
  const filterHeadline = appData.filterHeadline ?? null
  const handleMapLoad = map => {
    setMapRef(map)
  }

  React.useEffect(() => {
    if (!mapRef) return
    const filtered = appData.projects.filter(m => m.status.value === selectedCategory.value)
    if (filtered.length === 0) return
    if (filtered.length === 1) {
      mapRef.setCenter(filtered[0].position)
      mapRef.setZoom(15)
      return
    }
    const bounds = new window.google.maps.LatLngBounds()
    filtered.forEach(m => bounds.extend(m.map_location))
    mapRef.fitBounds(bounds, 60)
  }, [selectedCategory, mapRef])

  return (
    <>
      <div className="map-module-outer">
        <div className="map-filters container-fluid">
          {filterHeadline && <h2>{filterHeadline}</h2>}
          <nav>
            {categories.map(cat => (
              <a
                key={cat.value}
                href="#"
                onClick={e => {
                  e.preventDefault()
                  setSelectedCategory(cat)
                }}
                style={{
                  color: selectedCategory.value === cat.value ? '#d40f7d' : '#eee',
                }}
              >
                {cat.label && cat.label === 'Sold' ? 
                  <>Past <span class="d-none d-md-inline">Projects</span></> :  
                  cat.label
                }
              </a>
            ))}
          </nav>
        </div>
        <MapModule projectData={appData.projects} selectedCategory={selectedCategory} handleMapLoad={handleMapLoad} />
      </div>

      <ProjectInfoModule projectData={appData.projects} selectedCategory={selectedCategory} />
    </>
  )
}

root.render(
  <App />
)